import { BookOpenText, Link2, Plus, Send, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { ApiError, type ChatCitation, type ChatTurn } from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";

/**
 * CHAT-2 — ask anything, and every answer says where it came from.
 *
 * <b>The citation chips are the feature, not decoration.</b> They come from the Server's
 * record of what the grounding tools returned, never from anything the model wrote, so a chip
 * can promise that what it names is really there and really opens. An answer with no chips is
 * an answer with no sources behind it, and that is worth seeing at a glance.
 *
 * <b>A chip says which altitude it is.</b> An insight is something a person actually said in
 * one meeting; a belief is what the world view currently holds after weighing many sources.
 * They lead to different places — a source, or a page — and reading them as the same thing is
 * how a synthesized belief becomes an invented quotation.
 *
 * <b>The answer streams.</b> Not through `EventSource`, which cannot carry the bearer token
 * every route here needs, but through `fetch` and a reader — see `ApiClient.ask`. What that
 * buys is the first sentence appearing in a second rather than the whole answer landing after
 * twenty, which for a question that runs several searches is the difference between waiting
 * and wondering whether it is broken.
 *
 * <b>Simple mode is this screen in a larger frame</b> (UI-16), not a second one. It is called
 * **Ask** there, the column is narrower and the type is bigger, and that is the whole
 * difference: the citations, the streaming and the empty state are the feature and are not
 * machinery, so none of them is hidden from a reader who never asked how any of it works.
 */
export function ChatPage({ simple = false }: { simple?: boolean } = {}) {
  const { api, conversations, chatAvailable, refreshConversations } = useApp();
  const [params, setParams] = useSearchParams();
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [question, setQuestion] = useState("");
  const [streaming, setStreaming] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const conversationId = params.get("c");
  const bottom = useRef<HTMLDivElement>(null);

  const load = useCallback(
    async (id: string) => {
      try {
        setTurns(await api.getConversation(id));
        setProblem(null);
      } catch (failure) {
        setProblem(failure instanceof ApiError ? failure.message : "That conversation could not be read.");
      }
    },
    [api],
  );

  useEffect(() => {
    if (!conversationId) {
      setTurns([]);
      return;
    }

    void load(conversationId);
  }, [conversationId, load]);

  // The newest turn, kept in view while an answer is being written. Guarded because
  // `scrollIntoView` is a real browser's, and this component also renders in jsdom.
  useEffect(() => {
    bottom.current?.scrollIntoView?.({ block: "end" });
  }, [turns, streaming]);

  async function ask(event: React.FormEvent) {
    event.preventDefault();

    const asked = question.trim();
    if (asked.length === 0 || busy) return;

    setBusy(true);
    setProblem(null);
    setStreaming("");

    try {
      // A question with no thread starts one, so nobody has to press "New conversation"
      // before they can type — the first question is what a conversation is.
      const id = conversationId ?? (await api.startConversation()).id;
      if (!conversationId) setParams({ c: id }, { replace: true });

      setQuestion("");

      let answer = "";
      let citations: ChatCitation[] = [];
      let refused: string | null = null;

      await api.ask(id, asked, (frame) => {
        if (frame.type === "text") {
          answer += frame.text;
          setStreaming(answer);
        } else if (frame.type === "done") {
          answer = frame.text;
          citations = frame.citations;
        } else {
          refused = frame.message;
        }
      });

      if (refused) {
        // The Server committed nothing, so the question is not in the thread either. Give it
        // back rather than making somebody retype it.
        setProblem(refused);
        setQuestion(asked);
      } else {
        setTurns((current) => [
          ...current,
          turn(current.length, "user", asked, []),
          turn(current.length + 1, "assistant", answer, citations),
        ]);
        refreshConversations();
      }
    } catch (failure) {
      setProblem(failure instanceof ApiError ? failure.message : "The answer could not be reached.");
      setQuestion(asked);
    } finally {
      setStreaming(null);
      setBusy(false);
    }
  }

  async function start() {
    const conversation = await api.startConversation();
    setParams({ c: conversation.id });
    setTurns([]);
    setProblem(null);
    refreshConversations();
  }

  async function remove(id: string) {
    await api.deleteConversation(id);
    if (id === conversationId) setParams({});
    refreshConversations();
  }

  if (!chatAvailable) {
    return (
      <div className="mx-auto flex max-w-[760px] flex-col gap-[var(--space-3)] px-[var(--space-8)] py-[var(--space-8)]">
        <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          {simple ? "Ask" : "Chat"}
        </h1>
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
          Chat answers from your own material through the same tools an outside agent uses, and this
          deployment has no MCP server configured — so there is nothing for an answer to stand on. A
          chat that cannot reach your sources would be a chat that guesses, which is worse than one
          that is honestly unavailable.
        </p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "mx-auto flex h-full flex-col gap-[var(--space-4)] px-[var(--space-8)] py-[var(--space-8)]",
        simple ? "max-w-[760px]" : "max-w-[1120px]",
      )}
    >
      <header className="flex flex-wrap items-baseline gap-[var(--space-3)]">
        <h1
          className={cn(
            "font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]",
            simple ? "text-[40px] leading-[1.1]" : "text-[length:var(--text-3xl)]",
          )}
        >
          {simple ? "Ask" : "Chat"}
        </h1>
        <p
          className={cn(
            "text-[var(--text-secondary)]",
            simple ? "text-[length:var(--text-lg)] leading-[1.6]" : "text-[length:var(--text-sm)]",
          )}
        >
          Ask about anything you&rsquo;ve captured — every answer cites its source.
        </p>
        <div className="ml-auto flex items-center gap-[var(--space-2)]">
          {conversationId && (
            <button
              type="button"
              onClick={() => void remove(conversationId)}
              className="inline-flex items-center gap-[6px] rounded-[var(--radius-md)] border border-[var(--border-default)] px-[12px] py-[6px] text-[length:var(--text-xs)] text-[var(--text-secondary)] hover:bg-[var(--surface-2)]"
            >
              <Trash2 size={13} aria-hidden />
              Delete conversation
            </button>
          )}
          <button
            type="button"
            onClick={() => void start()}
            className="inline-flex items-center gap-[6px] rounded-[var(--radius-md)] border border-[var(--border-default)] px-[12px] py-[6px] text-[length:var(--text-xs)] text-[var(--text-primary)] hover:bg-[var(--surface-2)]"
          >
            <Plus size={13} aria-hidden />
            New conversation
          </button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-[var(--space-4)] overflow-y-auto pb-[var(--space-2)]">
        {turns.length === 0 && streaming === null && (
          <p className="py-[var(--space-12)] text-center text-[length:var(--text-sm)] text-[var(--text-secondary)]">
            {conversations.length === 0
              ? "Nothing asked yet. Every answer here is built from your own meetings, documents and notes."
              : "Ask something, or pick a conversation from the panel."}
          </p>
        )}

        {turns.map((entry) => (
          <Bubble key={entry.id} turn={entry} simple={simple} />
        ))}

        {streaming !== null && (
          <div className="max-w-[75%] self-start rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-[var(--space-4)] py-[var(--space-3)]">
            {streaming.length === 0 ? (
              <Typing />
            ) : (
              <p className="whitespace-pre-wrap text-[length:var(--text-sm)] leading-[1.6] text-[var(--text-primary)]">
                {streaming}
              </p>
            )}
          </div>
        )}

        <div ref={bottom} />
      </div>

      {problem && (
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {problem}
        </p>
      )}

      <form onSubmit={ask} className="flex items-center gap-[var(--space-2)]">
        <input
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder="Ask Factorium…"
          aria-label="Ask Factorium"
          disabled={busy}
          className={cn(
            "flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-1)] px-[var(--space-4)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]",
            simple ? "py-[14px] text-[length:var(--text-md)]" : "py-[10px] text-[length:var(--text-sm)]",
          )}
        />
        <button
          type="submit"
          disabled={busy || question.trim().length === 0}
          className="inline-flex items-center gap-[6px] rounded-[var(--radius-md)] bg-[var(--primary)] px-[var(--space-4)] py-[10px] text-[length:var(--text-sm)] font-semibold text-[var(--primary-foreground)] disabled:opacity-50"
        >
          <Send size={14} aria-hidden />
          Send
        </button>
      </form>
    </div>
  );
}

/** One turn. The side and the fill are what say who is speaking. */
function Bubble({ turn, simple = false }: { turn: ChatTurn; simple?: boolean }) {
  const mine = turn.role === "user";

  return (
    <div
      className={cn(
        "flex max-w-[75%] flex-col gap-[var(--space-2)] rounded-[var(--radius-lg)] px-[var(--space-4)] py-[var(--space-3)]",
        mine
          ? "self-end bg-[var(--primary)] text-[var(--primary-foreground)]"
          : "self-start border border-[var(--border-subtle)] bg-[var(--surface-glass)]",
      )}
    >
      <p
        className={cn(
          "whitespace-pre-wrap leading-[1.6]",
          simple ? "text-[length:var(--text-md)]" : "text-[length:var(--text-sm)]",
          mine ? "text-[var(--primary-foreground)]" : "text-[var(--text-primary)]",
        )}
      >
        {turn.content}
      </p>

      {turn.citations.length > 0 && (
        <ul className="flex flex-wrap gap-[6px]">
          {turn.citations.map((citation) => (
            <li key={citation.searchDocumentId}>
              <Chip citation={citation} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * One source, as something to open.
 *
 * A belief goes to the page it lives on; a quote goes to the source it was read from. Both
 * addresses are composed by the Server from the columns the index already carries, so a
 * renamed page cannot leave a chip pointing at nothing.
 */
function Chip({ citation }: { citation: ChatCitation }) {
  const belief = citation.altitude === "wiki_section";

  const to = belief
    ? `/world-view/${citation.address}`
    : citation.sourceMaterialId
      ? `/sources/${citation.sourceMaterialId}`
      : "/insights";

  return (
    <Link
      to={to}
      title={belief ? "What your world view holds" : "What was said, and where"}
      className="inline-flex items-center gap-[5px] rounded-[var(--radius-full)] border border-[var(--border-subtle)] bg-[var(--surface-2)] px-[9px] py-[3px] text-[length:var(--text-xs)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
    >
      {belief ? <BookOpenText size={11} aria-hidden /> : <Link2 size={11} aria-hidden />}
      <span>{citation.title}</span>
      {citation.sourceDate && (
        <span className="text-[var(--text-muted)]">{formatDate(citation.sourceDate)}</span>
      )}
    </Link>
  );
}

/** The handoff's `omTypingDot`: three dots on one keyframe, offset by delay. */
function Typing() {
  return (
    <span className="flex items-center gap-[4px]" aria-label="Thinking" role="status">
      {[0, 1, 2].map((dot) => (
        <span
          key={dot}
          className="size-[6px] rounded-full bg-[var(--text-muted)]"
          style={{ animation: `omTypingDot 1.2s ${dot * 0.15}s infinite ease-in-out` }}
        />
      ))}
    </span>
  );
}

/**
 * A turn appended locally after an answer lands.
 *
 * The alternative is re-reading the whole thread from the Server, which is a round trip for
 * rows this browser just watched being written. The ids are local and only have to be unique
 * within the list React is keying.
 */
function turn(
  sequence: number,
  role: "user" | "assistant",
  content: string,
  citations: ChatCitation[],
): ChatTurn {
  return {
    id: `local-${sequence}`,
    sequence,
    role,
    content,
    createdAt: new Date().toISOString(),
    citations,
  };
}
